import React, { ReactNode } from "react";
import {
  FiCalendar,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiActivity,
  FiClipboard,
  FiClock,
  FiAlertCircle,
} from "react-icons/fi";
import { Avaliation, WorkoutPlan } from "../../../types";
import utils from "../../../helpers/utils";

/**
 * Componentes auxiliares para o StudentManagementModal
 */

/** Classe base unificada para cards de listagem (mobile-first) */
export const CARD_BASE_CLASS =
  "rounded-xl border border-stroke bg-white p-3 shadow-sm dark:border-strokedark dark:bg-boxdark sm:p-4 md:p-5 md:rounded-2xl transition-all touch-manipulation active:scale-[0.99] md:hover:shadow-lg md:hover:-translate-y-0.5";

/** Classe de grid padronizada para listagens */
export const LIST_GRID_CLASS =
  "grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3";

interface SectionHeaderProps {
  readonly title: string;
  readonly description?: string;
  readonly actionButton: ReactNode;
}

export function SectionHeader({
  title,
  description,
  actionButton,
}: SectionHeaderProps) {
  return (
    <div className="flex flex-col gap-2 sm:gap-3 md:gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h4 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 dark:text-white">
          {title}
        </h4>
        {description && (
          <p className="mt-0.5 sm:mt-1 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
            {description}
          </p>
        )}
      </div>
      <div className="w-full sm:w-auto flex-shrink-0">
        {actionButton}
      </div>
    </div>
  );
}

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  icon: React.ElementType;
  label: string;
  count?: number;
}

export function TabButton({
  active,
  onClick,
  icon: Icon,
  label,
  count,
}: TabButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        group relative flex flex-shrink-0 items-center justify-center gap-1.5 sm:gap-2
        min-h-[44px] px-3 py-2.5 rounded-xl
        sm:min-h-0 sm:px-4 sm:py-3 md:px-5 md:py-3.5 sm:rounded-2xl
        text-xs sm:text-sm md:text-base font-semibold
        transition-all duration-200 ease-out
        touch-manipulation whitespace-nowrap
        active:scale-[0.97] md:active:scale-100
        border border-transparent
        snap-center
        ${
          active
            ? "text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900/40 border-purple-200/60 dark:border-purple-700/50 shadow-sm"
            : "text-gray-600 dark:text-gray-400 bg-gray-50/80 dark:bg-gray-800/50 border-gray-200/60 dark:border-gray-700/50 active:bg-gray-100 dark:active:bg-gray-700/50 md:hover:bg-gray-100 md:dark:hover:bg-gray-700/50 md:hover:text-gray-900 md:dark:hover:text-gray-200"
        }
      `}
    >
      <div className="flex items-center justify-center gap-1.5 sm:gap-2">
        <span
          className={`
            flex items-center justify-center rounded-lg p-0.5 sm:p-1
            transition-transform duration-200
            ${active ? "bg-purple-200/60 dark:bg-purple-700/40" : ""}
          `}
        >
          <Icon
            className={`w-4 h-4 sm:w-[18px] sm:h-[18px] md:w-5 md:h-5 ${
              active ? "text-purple-600 dark:text-purple-400" : "text-current"
            }`}
          />
        </span>
        <span className="whitespace-nowrap">{label}</span>
        {count !== undefined && (
          <span
            className={`
              flex items-center justify-center
              min-w-[18px] h-[18px] sm:min-w-[20px] sm:h-5 md:min-w-[22px] md:h-[22px]
              rounded-full px-1 sm:px-1.5
              text-[10px] sm:text-xs font-bold
              transition-all duration-200
              ${
                active
                  ? "bg-purple-600 text-white dark:bg-purple-500"
                  : "bg-gray-200/80 text-gray-600 dark:bg-gray-600 dark:text-gray-300"
              }
            `}
          >
            {count}
          </span>
        )}
      </div>
      {active && (
        <span
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 sm:w-8 h-0.5 sm:h-1 bg-purple-600 dark:bg-purple-500 rounded-full"
          aria-hidden
        />
      )}
    </button>
  );
}

interface EmptyStateProps {
  icon: React.ElementType;
  title: string;
  description: string;
}

export function EmptyState({
  icon: Icon,
  title,
  description,
}: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-white py-8 px-3 dark:border-gray-700 dark:bg-boxdark sm:py-12 sm:px-4 md:py-16 md:rounded-2xl">
      <div className="rounded-xl bg-gradient-to-br from-purple-100 to-purple-100 p-3 dark:from-purple-900/30 dark:to-purple-900/30 sm:p-4 md:p-6 md:rounded-2xl">
        <Icon
          size={32}
          className="w-8 h-8 text-gray-400 dark:text-gray-600 sm:w-10 sm:h-10 md:w-12 md:h-12"
        />
      </div>
      <p className="mt-3 text-sm font-bold text-gray-900 dark:text-white text-center sm:mt-4 sm:text-base md:mt-5 md:text-lg">
        {title}
      </p>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-center max-w-md px-2 sm:mt-1.5 sm:text-sm md:mt-2">
        {description}
      </p>
    </div>
  );
}

interface AvaliationCardProps {
  avaliation: Avaliation;
  pesoDisplay: string;
  imcDisplay: string;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export function AvaliationCard({
  avaliation,
  pesoDisplay,
  imcDisplay,
  onView,
  onEdit,
  onDelete,
}: AvaliationCardProps) {
  return (
    <div className={`group relative overflow-hidden ${CARD_BASE_CLASS}`}>
      <div className="relative">
        {/* Date Header - Mobile First */}
        <div className="mb-2 sm:mb-3 md:mb-4 flex items-center justify-between">
          <div className="flex items-center gap-1.5 sm:gap-2">
            <div className="rounded-md sm:rounded-lg bg-purple-100 p-1 sm:p-1.5 md:p-2 dark:bg-purple-900/30">
              <FiCalendar
                size={12}
                className="sm:w-[14px] sm:h-[14px] md:w-4 md:h-4 text-purple-600 dark:text-purple-400"
              />
            </div>
            <p className="text-xs sm:text-sm md:text-base font-bold text-gray-900 dark:text-white">
              {utils.formatDate(
                new Date(avaliation.date || avaliation.registe || ""),
              )}
            </p>
          </div>
        </div>

        {/* Metrics Grid - Mobile First */}
        <div className="mb-2 sm:mb-3 md:mb-5 grid grid-cols-2 gap-2 sm:gap-3 md:gap-4">
          <div className="rounded-md sm:rounded-lg md:rounded-xl bg-gradient-to-br from-purple-50 to-purple-100 p-2 sm:p-3 md:p-4 dark:from-purple-900/20 dark:to-purple-800/20">
            <p className="text-[9px] sm:text-[10px] md:text-xs font-semibold uppercase tracking-wide text-purple-700 dark:text-purple-400">
              Peso
            </p>
            <p className="mt-0.5 sm:mt-0.5 md:mt-1 text-lg sm:text-xl md:text-2xl font-bold text-purple-900 dark:text-purple-300">
              {pesoDisplay}
            </p>
          </div>
          <div className="rounded-md sm:rounded-lg md:rounded-xl bg-gradient-to-br from-purple-50 to-purple-100 p-2 sm:p-3 md:p-4 dark:from-purple-900/20 dark:to-purple-800/20">
            <p className="text-[9px] sm:text-[10px] md:text-xs font-semibold uppercase tracking-wide text-purple-700 dark:text-purple-400">
              IMC
            </p>
            <p className="mt-0.5 sm:mt-0.5 md:mt-1 text-lg sm:text-xl md:text-2xl font-bold text-purple-900 dark:text-purple-300">
              {imcDisplay}
            </p>
          </div>
        </div>

        {/* Action Buttons - Mobile First */}
        <div className="flex gap-1.5 sm:gap-2">
          <button
            onClick={onView}
            className="flex flex-1 items-center justify-center min-h-[44px] gap-1 rounded-lg bg-gradient-to-r from-purple-600 to-purple-700 py-2 text-[11px] font-semibold text-white transition-all active:scale-95 touch-manipulation sm:min-h-0 sm:gap-1.5 sm:rounded-lg sm:text-xs md:gap-2 md:py-2.5 md:text-sm md:hover:shadow-lg md:hover:scale-105"
          >
            <FiEye
              size={14}
              className="sm:w-[14px] sm:h-[14px] md:w-4 md:h-4"
            />
            <span className="hidden sm:inline">Visualizar</span>
            <span className="sm:hidden">Ver</span>
          </button>
          <button
            onClick={onEdit}
            className="flex items-center justify-center min-h-[44px] min-w-[44px] rounded-lg border-2 border-purple-200 p-2 text-purple-600 transition-all active:bg-purple-50 active:scale-95 touch-manipulation dark:border-purple-800 dark:text-purple-400 dark:active:bg-purple-900/20 sm:min-h-0 sm:min-w-0 sm:p-2 md:p-2.5 md:hover:bg-purple-50 md:hover:scale-105 md:dark:hover:bg-purple-900/20"
            aria-label="Editar avaliação"
          >
            <FiEdit2
              size={14}
              className="sm:w-[14px] sm:h-[14px] md:w-4 md:h-4"
            />
          </button>
          <button
            onClick={onDelete}
            className="flex items-center justify-center min-h-[44px] min-w-[44px] rounded-lg border-2 border-red-200 p-2 text-red-600 transition-all active:bg-red-50 active:scale-95 touch-manipulation dark:border-red-800 dark:text-red-400 dark:active:bg-red-900/20 sm:min-h-0 sm:min-w-0 sm:p-2 md:p-2.5 md:hover:bg-red-50 md:hover:scale-105 md:dark:hover:bg-red-900/20"
            aria-label="Deletar avaliação"
          >
            <FiTrash2
              size={14}
              className="sm:w-[14px] sm:h-[14px] md:w-4 md:h-4"
            />
          </button>
        </div>
      </div>
    </div>
  );
}

interface WorkoutPlanCardProps {
  plan: WorkoutPlan;
  onEdit: () => void;
  onDelete: () => void;
}

export function WorkoutPlanCard({
  plan,
  onEdit,
  onDelete,
}: WorkoutPlanCardProps) {
  // Verificar se a validade está expirada ou próxima de expirar
  const getValidadeStatus = () => {
    if (!plan.end_date) return null;

    const validadeDate = new Date(plan.end_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    validadeDate.setHours(0, 0, 0, 0);

    const diffDays = Math.floor(
      (validadeDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (diffDays < 0) {
      return { status: "expired", days: diffDays };
    } else if (diffDays <= 7) {
      return { status: "expiring", days: diffDays };
    }
    return { status: "valid", days: diffDays };
  };

  const validadeStatus = getValidadeStatus();

  return (
    <div className={`group relative overflow-hidden ${CARD_BASE_CLASS}`}>
      <div className="relative">
        {/* Header com Ícone e Título - Mobile First */}
        <div className="mb-2 sm:mb-3 md:mb-4 flex items-start justify-between gap-2 sm:gap-3">
          <div className="flex items-start gap-2 sm:gap-2.5 md:gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0 rounded-lg sm:rounded-xl bg-gradient-to-br from-purple-600 to-purple-600 p-1.5 sm:p-2 md:p-3 text-white shadow-sm">
              <FiClipboard
                size={16}
                className="sm:w-[18px] sm:h-[18px] md:w-6 md:h-6"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h5 className="text-sm sm:text-base md:text-lg font-bold text-gray-900 dark:text-white line-clamp-1">
                {plan.name}
              </h5>
              <div className="mt-0.5 sm:mt-1 flex flex-wrap items-center gap-1 sm:gap-1.5 md:gap-2 text-[9px] sm:text-[10px] md:text-xs text-gray-500 dark:text-gray-400">
                <span className="flex items-center gap-0.5 sm:gap-1 whitespace-nowrap">
                  <FiActivity size={10} className="sm:w-3 sm:h-3" />
                  {plan.exercises?.length || 0} treinos
                </span>
                <span className="hidden sm:inline">•</span>
                <span className="hidden sm:flex items-center gap-0.5 sm:gap-1 whitespace-nowrap">
                  <FiCalendar size={10} className="sm:w-3 sm:h-3" />
                  {utils.formatDate(new Date(plan.created_at || ""))}
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons - Mobile First */}
          <div className="flex gap-1 sm:gap-1.5 md:gap-2 flex-shrink-0">
            <button
              onClick={onEdit}
              className="flex items-center justify-center min-h-[44px] min-w-[44px] rounded-lg border-2 border-purple-200 p-2 text-purple-600 transition-all active:bg-purple-50 active:scale-95 touch-manipulation dark:border-purple-800 dark:text-purple-400 dark:active:bg-purple-900/20 sm:min-h-0 sm:min-w-0 sm:rounded-lg md:rounded-xl md:p-2.5 md:hover:bg-purple-50 md:hover:shadow-md md:hover:scale-105 md:dark:hover:bg-purple-900/20"
              aria-label="Editar plano"
            >
              <FiEdit2
                size={14}
                className="sm:w-4 sm:h-4 md:w-[18px] md:h-[18px]"
              />
            </button>
            <button
              onClick={onDelete}
              className="flex items-center justify-center min-h-[44px] min-w-[44px] rounded-lg border-2 border-red-200 p-2 text-red-600 transition-all active:bg-red-50 active:scale-95 touch-manipulation dark:border-red-800 dark:text-red-400 dark:active:bg-red-900/20 sm:min-h-0 sm:min-w-0 sm:rounded-lg md:rounded-xl md:p-2.5 md:hover:bg-red-50 md:hover:shadow-md md:hover:scale-105 md:dark:hover:bg-red-900/20"
              aria-label="Deletar plano"
            >
              <FiTrash2
                size={14}
                className="sm:w-4 sm:h-4 md:w-[18px] md:h-[18px]"
              />
            </button>
          </div>
        </div>

        {/* Informações de Validade - Mobile First */}
        {plan.end_date && (
          <div
            className={`mt-2 sm:mt-3 flex items-center gap-1.5 sm:gap-2 rounded-md sm:rounded-lg md:rounded-xl px-2 py-1.5 sm:px-3 sm:py-2 md:py-2.5 ${
              validadeStatus?.status === "expired"
                ? "bg-red-50 dark:bg-red-900/20"
                : validadeStatus?.status === "expiring"
                  ? "bg-orange-50 dark:bg-orange-900/20"
                  : "bg-blue-50 dark:bg-blue-900/20"
            }`}
          >
            {validadeStatus?.status === "expired" ? (
              <FiAlertCircle
                size={12}
                className="sm:w-[14px] sm:h-[14px] md:w-4 md:h-4 text-red-600 dark:text-red-400 flex-shrink-0"
              />
            ) : (
              <FiClock
                size={12}
                className={`sm:w-[14px] sm:h-[14px] md:w-4 md:h-4 flex-shrink-0 ${
                  validadeStatus?.status === "expiring"
                    ? "text-orange-600 dark:text-orange-400"
                    : "text-blue-600 dark:text-blue-400"
                }`}
              />
            )}
            <div className="flex-1 min-w-0">
              <p
                className={`text-[9px] sm:text-[10px] md:text-xs font-semibold ${
                  validadeStatus?.status === "expired"
                    ? "text-red-700 dark:text-red-400"
                    : validadeStatus?.status === "expiring"
                      ? "text-orange-700 dark:text-orange-400"
                      : "text-blue-700 dark:text-blue-400"
                }`}
              >
                Validade: {utils.formatDate(new Date(plan.end_date))}
              </p>
            </div>
            {validadeStatus?.status === "expired" && (
              <span className="flex-shrink-0 rounded-full bg-red-100 px-1.5 sm:px-2 py-0.5 text-[9px] sm:text-[10px] md:text-xs font-bold text-red-700 dark:bg-red-900/40 dark:text-red-400">
                Expirado
              </span>
            )}
            {validadeStatus?.status === "expiring" && (
              <span className="flex-shrink-0 rounded-full bg-orange-100 px-1.5 sm:px-2 py-0.5 text-[9px] sm:text-[10px] md:text-xs font-bold text-orange-700 dark:bg-orange-900/40 dark:text-orange-400">
                {validadeStatus.days}d
              </span>
            )}
          </div>
        )}

        {/* Data de criação no mobile - Mobile First */}
        <div className="mt-1.5 sm:mt-2 sm:hidden flex items-center gap-0.5 sm:gap-1 text-[9px] sm:text-[10px] text-gray-500 dark:text-gray-400">
          <FiCalendar size={10} />
          <span>
            Criado em {utils.formatDate(new Date(plan.created_at || ""))}
          </span>
        </div>
      </div>
    </div>
  );
}
